import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Button, message, Pagination, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { isMobile } from 'react-device-detect'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertFilled, CheckCircleOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
//import { gql, useQuery } from '@apollo/client'
import { InfoCxt } from '../../App'
import { Loading } from '../../components/Loading/defaultLoading'
import { formatRankStatus } from '../../utils/formatRankStatus'
import { getLastTestDate } from '../../utils/getLastTestDate'
import {
  ContentHeader,
  PaddingBox,
  PageBreadcrumb,
  PageSubtitle,
  PageTitle,
  TabHeader,
  TableBox,
  TablePaginationBox,
} from '../../components/Wrapper'
import { SummaryService } from '../../services/summary-services'
import { UserService } from '../../services/user-services'
import { UserData } from '../../types/user/types'
import { isHighBloodPressure } from '../../utils/isHighBloodPressure'

const { Text } = Typography

/*const GET_PATIENTS = gql`
  query GetPatients {
    physician {
      patients {
        id
        firstName
        userName
        lastActive
        patientDetail {
          priority
          diabetesType
          steroid
          blevelRisk
          pregnancy
          medicalConditions {
            title
          }
        }
      }
    }
  }
`*/

/*export interface PatientItemType {
  id: string
  firstName: string
  userName: string // can be treated as uid
  patientDetail: {
    priority: boolean
    diabetesType: 'TypeI' | 'TypeII' | 'Gestational' | 'Nil'
    steroid: number
    blevelRisk: number
    pregnancy: number
    medicalConditions: {
      title: string
    }[]
  }
  lastActive: string
}*/

interface UserScores {
  userId: string;
  name: string;
  data: UserData;
  rank1: string;
  rank2: string;
  frailty: string;
}

type PatientFilter =
  | 'abnormal'
  | 'high_blood'
  | 'rank1_ati'
  | 'rank2_ati'
  | 'prefrail'
  | 'frail'
  | 'visible'
  | 'hidden'

const FILTER_LABELS: Record<PatientFilter, string> = {
  abnormal: 'Any Abnormal Result',
  high_blood: 'High Blood Pressure',
  rank1_ati: 'Aim to Improve - Rank One',
  rank2_ati: 'Aim to Improve - Rank Two',
  prefrail: 'Pre-Frail',
  frail: 'Frail',
  visible: 'Visible Patients',
  hidden: 'Hidden Patients',
}

const FILTER_OPTIONS: { value: PatientFilter; label: string }[] = [
  { value: 'abnormal', label: FILTER_LABELS.abnormal },
  { value: 'high_blood', label: FILTER_LABELS.high_blood },
  { value: 'rank1_ati', label: FILTER_LABELS.rank1_ati },
  { value: 'rank2_ati', label: FILTER_LABELS.rank2_ati },
  { value: 'prefrail', label: FILTER_LABELS.prefrail },
  { value: 'frail', label: FILTER_LABELS.frail },
]

// Admin-only: filters by the hiddenFromPortal flag rather than clinical results.
const ADMIN_FILTER_OPTIONS: { value: PatientFilter; label: string }[] = [
  { value: 'visible', label: FILTER_LABELS.visible },
  { value: 'hidden', label: FILTER_LABELS.hidden },
]

/* -------------------------------- react fc -------------------------------- */

const Patient: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { token: loginToken, role } = useContext(InfoCxt)
  const filter = searchParams.get('filter') as PatientFilter | null
  /*const { loading, data: queryData } = useQuery(GET_PATIENTS, {
    context: { headers: { Authorization: loginToken } },
  })

  const patientsQueryData: PatientItemType[] = queryData?.physician?.patients || []
  const patientData = patientsQueryData*/
  const summaryService = SummaryService.getInstance()
  const userService = UserService.getInstance()
  const [patientData, setPatientData] = useState<UserScores[]>([])
  const [loading, setLoading] = useState(true)

  const [currentPage, setCurrentPage] = useState(1)

  const isAdmin = role === 'ROLE_ADMIN' || !!process.env.REACT_APP_NO_LOGIN

  const isHidden = (record: UserScores) => record.data?.otherInformation?.hiddenFromPortal === true

  const rowKeyOf = (record: UserScores) => `${record.userId}-${record.name}`

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const setHiddenForRecords = async (records: UserScores[], hidden: boolean) => {
    try {
      await Promise.all(records.map((record) => userService.updateVisibility(record.userId, hidden)))
      const targetIds = new Set(records.map((record) => record.userId))
      setPatientData((previous) =>
        previous.map((patient) =>
          targetIds.has(patient.userId)
            ? {
                ...patient,
                data: {
                  ...patient.data,
                  otherInformation: { ...(patient.data.otherInformation ?? {}), hiddenFromPortal: hidden },
                },
              }
            : patient
        )
      )
      const count = records.length
      const who = count === 1 ? records[0].name || records[0].userId : `${count} patients`
      message.success(
        hidden ? `${who} now hidden from doctors and organizations.` : `${who} visible again.`
      )
      setSelectedRowKeys([])
    } catch (err) {
      console.error(err)
      message.error('Failed to update visibility. Please try again later.')
    }
  }

  const toggleHidden = (record: UserScores) => setHiddenForRecords([record], !isHidden(record))

  useEffect(() => {
      const fetchData = async () => {
        //try {
          setLoading(true);
          const [rank1Records, rank2Records, rank2FrailtyRecords, usersData] = await Promise.all([
            summaryService.getAllRank1(),
            summaryService.getAllRank2(),
            summaryService.getAllRank2Frailty(),
            userService.getAll(),
          ]);
  
          // Map users to scores
          const scores: UserScores[] = usersData.map((data: UserData) => ({
            userId: data.userId,
            name: data.username,
            data: data,
            rank1: rank1Records[data.userId] || 'No Record',
            rank2: rank2Records[data.userId] || 'No Record',
            frailty: rank2FrailtyRecords[data.userId] || 'No Record',
          }))
          setPatientData(scores);
        //} catch (err) {
          // Handle errors
          //setError('Failed to fetch data');
          //console.error(err);
        //} finally {
          setLoading(false);
        //}
      };
  
      // Call the async function
      fetchData();
  }, []);

  const isAbnormal = (record: UserScores) =>
    isHighBloodPressure(record.data) ||
    record.rank1.toLowerCase() === 'aim to improve' ||
    record.rank2.toLowerCase() === 'aim to improve' ||
    record.frailty.toLowerCase() === 'pre-frail' ||
    record.frailty.toLowerCase() === 'frail'

  const visibleData = useMemo(
    () => (isAdmin ? patientData : patientData.filter((record) => !isHidden(record))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientData, isAdmin]
  )

  const filteredData = useMemo(() => {
    const patientData = visibleData
    if (!filter) return patientData
    switch (filter) {
      case 'abnormal':
        return patientData.filter(isAbnormal)
      case 'high_blood':
        return patientData.filter((record) => isHighBloodPressure(record.data))
      case 'rank1_ati':
        return patientData.filter((record) => record.rank1.toLowerCase() === 'aim to improve')
      case 'rank2_ati':
        return patientData.filter((record) => record.rank2.toLowerCase() === 'aim to improve')
      case 'prefrail':
        return patientData.filter((record) => record.frailty.toLowerCase() === 'pre-frail')
      case 'frail':
        return patientData.filter((record) => record.frailty.toLowerCase() === 'frail')
      case 'visible':
        return patientData.filter((record) => !isHidden(record))
      case 'hidden':
        return patientData.filter(isHidden)
      default:
        return patientData
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleData, filter])

  const selectedRecords = useMemo(
    () => filteredData.filter((record) => selectedRowKeys.includes(rowKeyOf(record))),
    [filteredData, selectedRowKeys]
  )

  const onFilterChange = (value: PatientFilter | undefined) => {
    setCurrentPage(1)
    if (!value) {
      setSearchParams({})
    } else {
      setSearchParams({ filter: value })
    }
  }

  const getAccountDisplay = (data: UserData): string => {
    if (data.email) return data.email
    const phoneNumber = data.otherInformation?.phoneNumber
    if (phoneNumber) {
      const countryCode = data.otherInformation?.countryCode ?? ''
      return `${countryCode}${phoneNumber}`
    }
    return ''
  }

  const getButtonColorByReading = (reading: string) => {
    switch(reading.toLowerCase()) {
      case 'doing well': return 'cyan'
      case 'aim to improve':
      case 'pre-frail': return 'orange'
      case 'frail': return 'danger'
      case 'error':
      case 'not finished':
      default: return 'default'
    }
  }

  const columns: ColumnsType<UserScores> = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: '200px',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string, record: UserScores) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {name}
          {isAdmin && isHidden(record) && <Tag style={{ marginInlineEnd: 0 }}>Hidden</Tag>}
        </span>
      ),
    },
    {
      title: 'Patient UID',
      dataIndex: 'userId',
      sorter: (a, b) => a.userId.localeCompare(b.userId),
    },
    {
      title: 'Account',
      render: (_, record) => {
        const account = getAccountDisplay(record.data)
        return account || '—'
      },
      sorter: (a, b) => (getAccountDisplay(a.data) || '').localeCompare(getAccountDisplay(b.data) || ''),
    },
    {
      title: 'Rank 1',
      key: 'rank1',
      sorter: (a, b) => a.rank1.localeCompare(b.rank1),
      render: (_, record: any) => (
        <Tooltip title='View Rank 1 Tests' placement='top'>
          <Button
            color={getButtonColorByReading(record.rank1)}
            variant="outlined"
            onClick={() => navigate(`/patient/${record.userId}`)}>
            {formatRankStatus(record.rank1)}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Rank 2',
      key: 'rank2',
      sorter: (a, b) => a.rank2.localeCompare(b.rank2),
      render: (_, record: any) => (
        <Tooltip title='View Rank 2 Tests' placement='top'>
          <Button
            color={getButtonColorByReading(record.rank2)}
            variant="outlined"
            onClick={() => navigate(`/patient/${record.userId}`)}>
            {formatRankStatus(record.rank2)}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Physical Frailty',
      key: 'frailty',
      sorter: (a, b) => a.frailty.localeCompare(b.frailty),
      render: (_, record: any) => (
        <Tooltip title='View Frailty Tests' placement='top'>
          <Button
            color={getButtonColorByReading(record.frailty)}
            variant="outlined"
            onClick={() => navigate(`/patient/${record.userId}`)}>
            {formatRankStatus(record.frailty)}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Blood Pressure',
      key: 'bp',
      sorter: (a, b) => Number(isHighBloodPressure(a.data)) - Number(isHighBloodPressure(b.data)),
      render: (_, record: any) => (
        <Tooltip title='View Blood Pressure Tests' placement='top'>
          <Button
            color={isHighBloodPressure(record.data) ? 'danger' : 'default'}
            variant="outlined"
            icon={isHighBloodPressure(record.data) ? <AlertFilled /> : <CheckCircleOutlined />}
            onClick={() => navigate(`/patient/${record.userId}`)}>
          </Button>
        </Tooltip>
      ),
    },
    /*{
      title: 'Priority Queue',
      dataIndex: ['patientDetail', 'priority'],
      width: '180px',
      sorter: (a, b) => Number(a.patientDetail.priority) - Number(b.patientDetail.priority),
      render: (value) => (
        <Tag
          style={{
            fontSize: '12px',
            fontWeight: 400,
            border: `1px solid ${value ? '#7966FF' : '#FF0844'}`,
            background: `${value ? '#FAF9FF' : '#FFFBFC'}`,
          }}>
          <Text
            style={{
              color: value ? '#7966FF' : '#FF0844',
              fontSize: '12px',
              lineHeight: '20px',
            }}>
            {value ? 'Yes' : 'No'}
          </Text>
        </Tag>
      ),
    },
    {
      title: 'Diabetes Type',
      dataIndex: ['patientDetail', 'diabetesType'],
      sorter: (a, b) => String(a.patientDetail.diabetesType).localeCompare(String(b.patientDetail.diabetesType)),
    },
    {
      title: 'Medical Condition',
      dataIndex: ['patientDetail', 'medicalConditions'],
      width: '180px',
      sorter: (a, b) => a.patientDetail.medicalConditions.length - b.patientDetail.medicalConditions.length,
      render: (value) => value.length,
    },*/
    {
      title: 'Last Active',
      key: 'lastActive',
      sorter: (a, b) =>
        (getLastTestDate(a.data)?.valueOf() ?? 0) - (getLastTestDate(b.data)?.valueOf() ?? 0),
      render: (_, record: UserScores) => {
        const lastTest = getLastTestDate(record.data)
        return lastTest ? lastTest.format('DD MMM YYYY') : '-'
      },
    },
    ...(isAdmin
      ? [
          {
            title: 'Visibility',
            key: 'visibility',
            render: (_: unknown, record: UserScores) => (
              <Popconfirm
                title={isHidden(record) ? 'Show this patient again?' : 'Hide this patient?'}
                description={
                  isHidden(record)
                    ? 'They will reappear for doctors and organizations.'
                    : 'Doctors and organizations will no longer see them. You can unhide anytime.'
                }
                okText={isHidden(record) ? 'Show' : 'Hide'}
                onConfirm={() => toggleHidden(record)}>
                <Tooltip title={isHidden(record) ? 'Hidden — click to show' : 'Visible — click to hide'} placement='top'>
                  <Button icon={isHidden(record) ? <EyeInvisibleOutlined /> : <EyeOutlined />} />
                </Tooltip>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='dash-board-page'>
          <ContentHeader white={false} border>
            <PageBreadcrumb path={[{ label: 'Home', href: '/' }, 'View Patient']} />
            <TabHeader padding={false}>
              <PageTitle text='View Patient' />
            </TabHeader>
          </ContentHeader>
          <PaddingBox>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Text type='secondary'>Filter:</Text>
              <Select
                allowClear
                placeholder='All Patients'
                style={{ minWidth: 240 }}
                value={filter ?? undefined}
                options={[
                  { value: 'all', label: 'All Patients' },
                  ...FILTER_OPTIONS,
                  ...(isAdmin ? [{ label: 'Visibility', options: ADMIN_FILTER_OPTIONS }] : []),
                ]}
                onChange={(value: string | undefined) =>
                  onFilterChange(value === 'all' ? undefined : (value as PatientFilter | undefined))
                }
              />
              <Text type='secondary'>
                {filteredData.length} of {patientData.length} patients
              </Text>
            </div>
            {isAdmin && selectedRowKeys.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Text type='secondary'>{selectedRowKeys.length} selected</Text>
                <Popconfirm
                  title={`Hide ${selectedRowKeys.length} patient${selectedRowKeys.length === 1 ? '' : 's'}?`}
                  description='Doctors and organizations will no longer see them. You can unhide anytime.'
                  okText='Hide'
                  onConfirm={() => setHiddenForRecords(selectedRecords, true)}>
                  <Button icon={<EyeInvisibleOutlined />}>Hide Selected</Button>
                </Popconfirm>
                <Popconfirm
                  title={`Show ${selectedRowKeys.length} patient${selectedRowKeys.length === 1 ? '' : 's'}?`}
                  description='They will reappear for doctors and organizations.'
                  okText='Show'
                  onConfirm={() => setHiddenForRecords(selectedRecords, false)}>
                  <Button icon={<EyeOutlined />}>Show Selected</Button>
                </Popconfirm>
                <Button type='text' onClick={() => setSelectedRowKeys([])}>
                  Clear selection
                </Button>
              </div>
            )}
            <TableBox>
              <Table
                columns={[
                  ...columns,
                  {
                    title: '',
                    render: (_, { userId }) => (
                      <Space size='middle'>
                        <Button type='primary' ghost onClick={() => navigate(`/patient/${userId}`)}>
                          View details
                        </Button>
                      </Space>
                    ),
                  },
                ]}
                dataSource={filteredData}
                rowKey={rowKeyOf}
                rowSelection={
                  isAdmin
                    ? {
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                      }
                    : undefined
                }
                pagination={{
                  position: ['none'],
                  current: currentPage,
                }}
              />
            </TableBox>
            <div style={{ display: 'flex', justifyContent: 'end' }}>
              <TablePaginationBox>
                <Pagination
                  current={currentPage}
                  total={filteredData.length}
                  pageSize={10}
                  showLessItems={isMobile}
                  showSizeChanger={true}
                  onChange={(page) => setCurrentPage(page)}
                />
              </TablePaginationBox>
            </div>
          </PaddingBox>
        </div>
      )}
    </>
  )
}

export default Patient
